import { useLinkPromoPreview } from "../state/link-promo-preview";
import { totalAfterLinkPromo } from "../state/link-promo";
import { formatDue } from "../state/checkout-steps";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { checkoutLayoutSetting, resolveCheckoutPresentation } from "@vertu/shared";
import { useCheckoutReturn } from "./checkout/useCheckoutReturn";
import type {
  WidgetAiAccess,
  WidgetBrand,
  WidgetDefinition,
  WidgetEvent,
  WidgetEventCta,
  WidgetFunctionsCatalogue,
  WidgetPanel,
  WidgetPanelSlot,
  WidgetService,
  WidgetTenant,
  WidgetUnit,
} from "../types";
import { ServicePanel } from "./ServicePanel";
import { ChideskPanel } from "./ChideskPanel";
import { AvailabilityPanel } from "./AvailabilityPanel";
import { routesToAvailabilityPanel } from "../state/option-led";
import { CartPanel } from "./CartPanel";
import { ChideskResumeModal } from "./ChideskResumeModal";
import { AssistantModal } from "./AssistantModal";
import { GiftCardPurchasePanel } from "./GiftCardPurchasePanel";
import { EnquiryPanel } from "./EnquiryPanel";
import { EventsPanel } from "./EventsPanel";
import { CombosPanel } from "./CombosPanel";
import {
  comboConditionsLabel,
  comboPricingLabel,
  fetchPublishedCombos,
} from "../state/combos";
import { resolveAvailabilityBadge } from "../state/availability-badge";
import type { WidgetCombo } from "../types";
import { VenueIdentityContext, VenueMark, type VenueIdentity } from "./VenueMark";
import { heroBrandVars } from "../theme";
import {
  comboDeepLinkTarget,
  keepsActiveTab,
  resolveInitialLanding,
  resolveLandingTab,
  showsTabBar,
  type OptionLinkTarget,
} from "../state/landing";
import { FunctionsPanel } from "./FunctionsPanel";
import { useGuest } from "../state/guest";
import { useCart } from "../state/cart";
import { checkoutSummaryImages } from "../state/checkout-images";
import { computeCohostPairing, cohostNoticeText } from "../state/cohost";
import { trackGoogleAdsConversion } from "../state/tracking";
import { majorUnits, pushHostPurchase } from "../state/host-datalayer";
import { ServiceIcon } from "./ServiceIcon";
import { PanelBlock, type PseudoTab } from "./PanelBlock";
import { ToastContainer } from "./Toast";
import { WidgetLayer, WidgetLayerVars } from "./WidgetLayer";
import { pushToast } from "../state/toast";

// Pseudo-tab glyphs for the accordion rows (services use ServiceIcon).
function AccIcon({
  kind,
}: {
  kind: "events" | "functions" | "gift" | "enquire" | "packages";
}) {
  const p = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (kind === "events")
    return (
      <svg {...p}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  if (kind === "functions")
    return (
      <svg {...p}>
        <path d="M8 2h8" />
        <path d="M12 22V12" />
        <path d="M8 22h8" />
        <path d="M6 2c.5 4 2 7 6 7s5.5-3 6-7" />
      </svg>
    );
  if (kind === "gift")
    return (
      <svg {...p}>
        <polyline points="20 12 20 22 4 22 4 12" />
        <rect x="2" y="7" width="20" height="5" />
        <line x1="12" y1="22" x2="12" y2="7" />
        <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
        <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
      </svg>
    );
  if (kind === "packages")
    return (
      <svg {...p}>
        <path d="M21 8v13H3V8" />
        <path d="M1 3h22v5H1z" />
        <path d="M10 12h4" />
      </svg>
    );
  return (
    <svg {...p}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 5L2 7" />
    </svg>
  );
}

// Where a fresh mount lands, and where the post-booking redirect goes, is
// decided in state/landing.ts (pure; e2e/widget-landing-logic.spec.ts).

export function WidgetShell({
  tenant,
  widget,
  layout,
  activeBrand,
  brands,
  services,
  defaultService,
  panels,
  events,
  showEventsTab,
  embedDefaultTab,
  initialEventSlug,
  initialComboSlug,
  initialOption,
  functions,
  tenantSlug,
  widgetSlug,
  aiAssistant,
}: {
  tenant: WidgetTenant;
  widget: WidgetDefinition;
  layout: string;
  activeBrand: WidgetBrand | null;
  brands: WidgetBrand[];
  services: WidgetService[];
  defaultService: WidgetService | null;
  panels: WidgetPanel[];
  events: WidgetEvent[];
  showEventsTab: boolean;
  /** Tab named by ?tab= or data-default-tab. May be a service slug OR a
   *  pseudo-tab id (__events__ / __packages__ / __functions__ / __enquiries__). */
  embedDefaultTab?: string;
  /** Deep-link target — open the Events tab on this event's detail. */
  initialEventSlug?: string;
  /** Deep-link target (`?combo=<slug>`) — open the Packages tab on this
   *  package's detail; the packages list when it is not bookable here. */
  initialComboSlug?: string;
  /** Deep-link target (`?option=<slug>`): open this service's panel with the
   *  option chosen (state/landing.ts optionDeepLinkTarget). */
  initialOption?: OptionLinkTarget | null;
  /**
   * Function-venue catalogue from get_widget_config v14. Null when the
   * tenant has no function venues configured — the Functions tab stays
   * hidden in that case.
   */
  functions: WidgetFunctionsCatalogue | null;
  tenantSlug: string;
  widgetSlug: string;
  aiAssistant: WidgetAiAccess | null;
}) {
  // Deep-link target: ?event=<slug> (from the public hosted event page)
  // resolves to an event in this widget's curated list. When present we
  // land on the Events tab with that event's detail open. Falls back
  // silently if the slug isn't in the set or the tab is hidden.
  const initialEvent =
    showEventsTab && initialEventSlug
      ? events.find((e) => e.slug === initialEventSlug) ?? null
      : null;
  // The tenant's configured landing tab (settings.default_tab). Lets the home /
  // post-booking tab be a pseudo-tab (Events / Packages), not just a service.
  // A deep-linked event still wins; otherwise a valid pseudo-tab, else the
  // default service, else the first tab. Because a Paystack return is a fresh
  // mount, this is ALSO where the guest lands after finishing a booking — so
  // they return to the tenant's chosen tab instead of the first service.
  const configuredDefaultTab =
    typeof (widget.settings as Record<string, unknown> | null | undefined)?.default_tab === "string"
      ? ((widget.settings as Record<string, unknown>).default_tab as string)
      : null;
  // Optional post-booking landing tab. When set, a guest who just completed a
  // booking (a Paystack return OR the in-widget "Done") lands here instead of
  // the default tab — e.g. book a spa treatment, then land on Events to
  // cross-sell. Unset → unchanged behaviour (default tab / stay put).
  const configuredSuccessTab =
    typeof (widget.settings as Record<string, unknown> | null | undefined)?.success_tab === "string"
      ? ((widget.settings as Record<string, unknown>).success_tab as string)
      : null;
  const [activeId, setActiveId] = useState<string | null>(() => {
    // These gates are recomputed here rather than reused from further down
    // the component: those consts are declared AFTER this initialiser, so
    // referencing them would hit the temporal dead zone. Keep them in step.
    const canFunctions =
      (widget.functions_tab_enabled ?? true) &&
      (functions?.venues.length ?? 0) > 0;
    const canEnquiries =
      (widget.settings?.enquire_tab_enabled as boolean | undefined) !== false;
    const canGiftCards =
      (widget.settings?.gift_cards_tab_enabled as boolean | undefined) === true;
    // A deep-linked event, then a deep-linked package, then a tab named on the
    // URL (?tab=) or the embed (data-default-tab), which beats the widget's
    // configured default — each the more specific instruction.
    return resolveInitialLanding({
      eventMatched: initialEvent !== null,
      comboSlug: initialComboSlug,
      tabKey: embedDefaultTab || configuredDefaultTab,
      showEventsTab,
      services,
      defaultService,
      showFunctionsTab: canFunctions,
      showEnquiriesTab: canEnquiries,
      showGiftCardsTab: canGiftCards,
    });
  });
  // One-shot: feed the deep-linked event into EventsPanel's detail view on
  // mount, then clear it so navigating back to the list (or re-mounting the
  // tab) doesn't snap back to it. Mirrors pendingFunctionTypeId below.
  // Published combo packages — read directly (anon RLS exposes only
  // published+enabled), so no get_widget_config bump. The Packages tab
  // appears once the fetch lands with rows.
  const [combos, setCombos] = useState<WidgetCombo[]>([]);
  // Have combos finished loading? The Packages pseudo-tab only exists once
  // this fetch lands, so the "landing tab still valid?" fallback below waits
  // for this before it would ever reset a `__packages__` default.
  const [combosLoaded, setCombosLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchPublishedCombos(tenant.id).then(({ combos: rows, failed }) => {
      if (cancelled) return;
      // A read failure is not "this venue sells no packages". Say which one it
      // was, once, rather than letting the tab disappear without a trace.
      if (failed) {
        setCombos([]);
        setCombosLoaded(true);
        return;
      }
      // Only surface combos this widget can actually book: brand-scoped
      // combos must match the active brand, and every component's service
      // must be in this widget's visible service list — otherwise the
      // configurator would render permanently-disabled "contact the venue"
      // cards (multi-brand tenants share one combos table).
      const bookable = rows.filter(
        (c) =>
          (c.brand_id === null ||
            activeBrand === null ||
            c.brand_id === activeBrand.id) &&
          c.combo_components.length > 0 &&
          c.combo_components.every((cc) =>
            services.some((s) => s.id === cc.service_id),
          ),
      );
      setCombos(bookable);
      setCombosLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [tenant.id, activeBrand, services]);

  // Tenant controls for the Packages surface (widget_configs.settings —
  // same absent-means-default pattern as gift_cards/enquire below).
  // `packagesEnabled` hides the TAB only: the __packages__ panel stays
  // reachable from cross-sell surfaces so package links never dead-end.
  const packagesEnabled =
    (widget.settings?.packages_tab_enabled as boolean | undefined) !== false;
  const packagesLabel =
    (widget.settings?.packages_tab_label as string | undefined)?.trim() ||
    "Packages";
  // Tenant-set overrides for the Events + Enquire tab labels — same
  // absent-means-default pattern as packagesLabel above.
  const eventsLabel =
    (widget.settings?.events_tab_label as string | undefined)?.trim() ||
    "Events";
  const enquireLabel =
    (widget.settings?.enquire_tab_label as string | undefined)?.trim() ||
    "Enquire";
  // The assistant button carries OUR product name by default, which reads as
  // third-party branding inside a tenant's own site. Same absent-means-default
  // override as the tab labels above.
  const assistantLabel =
    (widget.settings?.assistant_label as string | undefined)?.trim() ||
    "Ask Vertu";
  // Tenant control for the per-slot availability badge ("{count} left") —
  // resolved once here, passed to every Service/Events panel.
  const availabilityBadge = resolveAvailabilityBadge(widget.settings);
  // serviceId → checkout add-on catalogue, for the cart's "Make it special"
  // strip. Only services that actually have add-ons appear.
  const serviceAddonsMap = useMemo(() => {
    const map: Record<string, import("../types").WidgetAddon[]> = {};
    for (const s of services) {
      if (s.addons && s.addons.length > 0) map[s.id] = s.addons;
    }
    return map;
  }, [services]);

  const packagesAutoCrossSell =
    (widget.settings?.packages_auto_cross_sell as boolean | undefined) !==
    false;

  const [pendingEventId, setPendingEventId] = useState<string | null>(
    initialEvent?.id ?? null,
  );
  // One-shot, like pendingEventId: the ?option= link's option is chosen when
  // its service's panel first mounts, then cleared, so coming back to the tab
  // later never picks it again.
  const [pendingOption, setPendingOption] = useState<OptionLinkTarget | null>(
    initialOption ?? null,
  );
  // The service whose "What's on" card opened the event detail. With the
  // Events tab off there is no events list to go back to, so the detail's
  // back link returns here instead.
  const [hostedEventFrom, setHostedEventFrom] = useState<string | null>(null);
  // Which offer the guest actually clicked. Without this, every route into the
  // packages panel landed on the browsing LIST — so clicking "Solo Stay"
  // opened a list, and none of Solo Stay's own defaults (1 guest, the days it
  // runs, its nights) were ever applied.
  const [pendingComboId, setPendingComboId] = useState<string | null>(null);
  // One-shot: a `?combo=<slug>` link opens that package's detail once the
  // packages have loaded (the Packages tab is already showing — see the
  // initial landing). Not bookable here, or unknown: the guest stays on the
  // packages list. Only a deep link that did not lose to an event link.
  const comboDeepLinkDoneRef = useRef(initialEvent !== null || !initialComboSlug);
  useEffect(() => {
    if (comboDeepLinkDoneRef.current || !combosLoaded) return;
    comboDeepLinkDoneRef.current = true;
    const id = comboDeepLinkTarget(initialComboSlug, combos);
    if (id) setPendingComboId(id);
  }, [combosLoaded, combos, initialComboSlug]);
  const [cartOpen, setCartOpen] = useState(false);
  // Adding an item asks "Who's booking?" BEFORE the cart drawer slides in.
  // Both are overlays; showing them together left the sheet dimmed behind the
  // drawer. askIdentityThen runs its callback immediately when the guest is
  // already known (or already declined for this cart), so the normal path is
  // unchanged — one extra step only the first time, only when anonymous.
  const { askIdentityThen } = useGuest();
  const openCartAfterIdentity = () => askIdentityThen(() => setCartOpen(true));
  // Pre-scopes the enquiry form to a service when opened via a service's
  // Enquire CTA; null = the general (event-kind) enquiry form.
  const [enquiryServiceId, setEnquiryServiceId] = useState<string | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  // catalog-drawer: which service's booking drawer is open.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // wizard: 0 = choose, 1 = book, 2 = enhance (add-ons).
  const [wizardStep, setWizardStep] = useState(0);
  // Anchor for the main booking panel. On mobile the sidebar/footer
  // panels stack BELOW the booking panel, so tapping a row down there
  // switches the active tab but leaves the viewport at the bottom —
  // the customer has to scroll up themselves to see what they picked.
  // We scroll this ref into view after every programmatic tab switch
  // (i.e. anything triggered from a panel row, not from the tab bar).
  const mainPanelRef = useRef<HTMLDivElement | null>(null);
  // Cross-sell context — when the customer taps a sidebar/footer panel
  // row to switch services, we stash the previous service's name so the
  // destination ServicePanel can show a "Continuing from X" banner.
  // Cleared when the customer manually taps a tab.
  const [continuingFrom, setContinuingFrom] = useState<string | null>(null);
  // Deep-link from a function_types_list panel row → switch to the
  // Functions tab AND pre-select the chip. FunctionsPanel reads this on
  // mount and clears it once consumed (one-shot).
  const [pendingFunctionTypeId, setPendingFunctionTypeId] = useState<
    string | null
  >(null);
  // What this page load found: back from Paystack (the confirmation, or
  // "Confirming your payment…" and then where it will arrive — never
  // silent), or a full-page checkout kept in this tab to restore
  // (useCheckoutReturn). A fresh mount is the only time either can happen.
  const checkoutReturn = useCheckoutReturn({
    tenantSlug: tenant.slug,
    openCart: () => setCartOpen(true),
    onConfirmed: (result) => {
      // Google Ads has no server-side path, so fire its conversion here for the
      // paid booking — with the paid amount + email for Enhanced Conversions.
      if (typeof result.amount_cents === "number") {
        trackGoogleAdsConversion({
          reference: result.primary_booking_reference ?? null,
          value: result.amount_cents / 100,
          currency: result.currency ?? "ZAR",
          email: result.customer.email ?? null,
        });
      }
      // The venue's own tags (an embed on its site) hear of every confirmed
      // booking, this paid one too — with what Paystack took — although
      // Vertu's own GA4 + Meta purchase for it go server-side
      // (state/host-datalayer.ts).
      pushHostPurchase({
        tenant: tenant.slug,
        reference: result.primary_booking_reference ?? null,
        value: majorUnits(result.amount_cents),
        currency: result.currency ?? "ZAR",
      });
      // Confirmed payment → land the background on the tenant's post-booking
      // success tab (if set), so the guest is on it once they close the
      // confirmation. Only on a confirmed payment, so a failed/pending
      // return never cross-sells.
      if (configuredSuccessTab) {
        const t = resolveLandingTab(
          configuredSuccessTab,
          showEventsTab,
          services,
          defaultService,
        );
        if (t) setActiveId(t);
      }
    },
  });
  // Each service's checkout layout setting (services.config.checkout_layout).
  const checkoutLayouts = useMemo(
    () => Object.fromEntries(services.map((svc) => [svc.id, checkoutLayoutSetting(svc.config)])),
    [services],
  );
  const presentation = useMemo(
    () => resolveCheckoutPresentation(widget.settings?.checkout_presentation),
    [widget.settings?.checkout_presentation],
  );
  const { items: cartItems } = useCart();
  const summaryImages = useMemo(
    () => checkoutSummaryImages(cartItems, services, events),
    [cartItems, services, events],
  );
  const aiEnabled = Boolean(aiAssistant?.enabled) && (aiAssistant?.balance_cents ?? 0) > 0;
  const active =
    services.find((s) => s.id === activeId) ?? services[0] ?? null;

  // The active tab's brand colours, scoped per the tenant's chosen reach
  // ("header" → just the hero; "panel" → the whole active-tab surface, the vars
  // cascading from .vt-panel). Multi-brand widgets have a null global
  // activeBrand, so each tab resolves its OWN brand here. Shared by BOTH panel
  // render paths — renderPanel() (wizard/accordion/drawer layouts) and the
  // inline two-column render below — so every layout tints identically and the
  // resolution can't drift out of sync between them again.
  const brandHeroProps = (svc: WidgetService | null) => {
    const b = brands.find((x) => x.id === svc?.brand_id) ?? activeBrand;
    const vars = heroBrandVars(b, widget) as CSSProperties;
    const panelScope =
      (widget.theme as { brandTintScope?: string } | null)?.brandTintScope ===
      "panel";
    return {
      heroBrand: b,
      heroStyle: panelScope ? undefined : vars,
      panelStyle: panelScope ? vars : undefined,
    };
  };

  const displayName = activeBrand?.name ?? tenant.name;
  // Header strap: an explicit settings.header_tagline wins — custom text, or
  // "" to hide the strap entirely. Absent = the legacy chain incl. the classic
  // default, so untouched widgets look the same.
  const settingsTagline = (
    widget.settings as { header_tagline?: unknown } | null
  )?.header_tagline;
  const displayTagline =
    typeof settingsTagline === "string"
      ? settingsTagline.trim() || null
      : (activeBrand?.tagline ?? widget.description ?? "Book · Experience · Enjoy");
  const logoSrc = activeBrand?.logo_url ?? tenant.logo_url;
  // The header's identity, also shown by the full-page checkout's top bar
  // (it covers this header while it is open).
  const venueIdentity = useMemo<VenueIdentity>(
    () => ({ title: displayName, tagline: displayTagline, logoSrc }),
    [displayName, displayTagline, logoSrc],
  );

  // Match scope on the activeId string (not the resolved Service). This
  // lets tenants scope panels to the virtual tabs (Gift cards, Enquire)
  // by adding the matching pseudo-id (__gift_cards__ / __enquiries__) to
  // a panel's scope_service_ids — same shape as real services.
  const visiblePanels = useMemo(
    () =>
      panels
        .filter(
          (p) =>
            !p.scope_service_ids ||
            p.scope_service_ids.length === 0 ||
            (activeId !== null && p.scope_service_ids.includes(activeId)),
        )
        .sort((a, b) => a.display_order - b.display_order),
    [panels, activeId],
  );

  const bySlot = (slot: WidgetPanelSlot) =>
    visiblePanels.filter((p) => p.slot === slot);

  const sidebarTop = bySlot("sidebar_top");
  const sidebarBottom = bySlot("sidebar_bottom");
  const belowMain = bySlot("below_main");
  const footerPanels = bySlot("footer");
  const hasSidebar = sidebarTop.length > 0 || sidebarBottom.length > 0;

  /**
   * Tab nav handler used both by TabBar (manual tap) and by PanelBlock
   * cross-sell rows (programmatic switch). Manual taps clear any pending
   * continuing-from banner; programmatic switches set it so the
   * destination ServicePanel can render "Continuing from X".
   */
  function handleSwitchService(
    serviceId: string,
    fromServiceId: string | null = null,
  ) {
    if (fromServiceId) {
      const fromService = services.find((s) => s.id === fromServiceId);
      setContinuingFrom(fromService?.name ?? null);
      // Programmatic switch from a sidebar/footer row — mobile users
      // were left scrolled at the bottom of the page. Wait one frame so
      // React commits the new active tab before we scroll, otherwise
      // scrollIntoView lands on stale layout.
      if (typeof window !== "undefined") {
        requestAnimationFrame(() => {
          mainPanelRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      }
    } else {
      setContinuingFrom(null);
    }
    // Any other move forgets the hosted event's way back
    // (handleSelectHostedEvent sets it again after calling this).
    setHostedEventFrom(null);
    setActiveId(serviceId);
  }

  /**
   * Handler for the + button on PanelBlock rows (sidebar + footer
   * cross-sell). Used to dump the row's unit straight into the cart
   * with no booking window — which broke every downstream system: no
   * starts_at/ends_at for conflict detection, no capacity hold, no
   * operating-hours check, no "what time?" data for checkout. The
   * cart drawer would show "Stoep — Table 1" with no time and the
   * server would have no idea what to confirm.
   *
   * Fix: switch to the target service's tab + carry forward the
   * "continuing from X" banner. The customer then completes the
   * normal date/time/party flow on that service's panel. Same
   * destination as tapping the row body — the + just becomes a
   * stylistic affordance.
   */
  function handleQuickAdd(service: WidgetService, _unit: WidgetUnit) {
    handleSwitchService(service.id, activeId);
    pushToast(`Switched to ${service.name} — pick your date below`);
  }

  // From a service's "What's on" list → open that event's detail in the
  // Events tab (service-hosted events show under both the service and the tab).
  // With the Events tab off the detail still opens (the landing safety net
  // keeps __events__ while there are events), and its back link returns to
  // this service.
  function handleSelectHostedEvent(eventId: string) {
    setPendingEventId(eventId);
    const from = active?.id ?? null;
    handleSwitchService("__events__", activeId);
    setHostedEventFrom(from);
  }

  // Back from that detail with no Events tab: the service it was opened
  // from, scrolled to its "What's on" list, where the guest was.
  function handleBackToHostService() {
    if (!hostedEventFrom) return;
    handleSwitchService(hostedEventFrom);
    if (typeof window !== "undefined") {
      requestAnimationFrame(() => {
        mainPanelRef.current
          ?.closest(".vt-widget")
          ?.querySelector(".vt-hosted-events")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }

  // Footer/sidebar "Plan a Wedding" / "Plan a Conference" rows route
  // here. Switch to the Functions tab and stash the function_type_id
  // so FunctionsPanel can pre-select it once mounted.
  function handleSelectFunctionType(functionTypeId: string) {
    setPendingFunctionTypeId(functionTypeId);
    handleSwitchService("__functions__", activeId);
  }

  // Event-only venues (e.g. The Audrium) offer an "Enquire about private
  // events" CTA that jumps to the Enquire tab.
  function handleEnquire() {
    setEnquiryServiceId(null);
    handleSwitchService("__enquiries__", activeId);
  }

  // Per-service Enquire CTA: jump to the Enquire tab pre-scoped to the service.
  function handleEnquireService(serviceId: string) {
    setEnquiryServiceId(serviceId);
    handleSwitchService("__enquiries__", activeId);
  }

  // A tenant-authored CTA on an event: jump to another service's tab or the
  // enquiry form. (url CTAs are plain external links handled in the panel.)
  function handleEventCta(cta: WidgetEventCta) {
    if (cta.kind === "enquiry") {
      if (showEnquiriesTab) handleEnquire();
      return;
    }
    if (cta.kind === "service_tab") {
      const svc = services.find((s) => s.slug === cta.target);
      if (svc) {
        handleSwitchService(svc.id, activeId);
      } else {
        // The target service isn't one of this widget's tabs (e.g. a cohost
        // requirement pointing at a service the tenant didn't enable here).
        // Never dead-end silently — tell the guest the venue will handle it.
        pushToast(
          "That booking can't be added here — the venue will arrange it with you.",
          "info",
        );
      }
    }
  }

  // Non-service tabs the customer can switch to (Functions, Events).
  // services_list panels merge these in so the "List of services" feels
  // complete — from the customer's POV they're just more options
  // alongside Spa, Dining, etc.
  const pseudoTabs: PseudoTab[] = useMemo(() => {
    const tabs: PseudoTab[] = [];
    if (showEventsTab) {
      tabs.push({ id: "__events__", name: eventsLabel, tagline: null });
    }
    if (combos.length > 0 && packagesEnabled) {
      tabs.push({ id: "__packages__", name: packagesLabel, tagline: null });
    }
    const functionsEnabled =
      (widget.functions_tab_enabled ?? true) &&
      (functions?.venues.length ?? 0) > 0;
    if (functionsEnabled) {
      tabs.push({
        id: "__functions__",
        name: widget.functions_tab_label?.trim() || "Functions",
        tagline: widget.functions_tab_tagline?.trim() ?? null,
      });
    }
    return tabs;
  }, [
    showEventsTab,
    widget,
    functions,
    combos,
    packagesEnabled,
    packagesLabel,
    eventsLabel,
  ]);

  // Renders the booking panel for a given tab/service id. The tab layout
  // renders this inline (below); the accordion layout calls it per expanded
  // row, so both reuse the exact same booking/cross-sell components.
  const renderPanel = (id: string | null) => {
    if (id === "__events__") {
      // No Events tab: a detail opened from a service's "What's on" goes back
      // to that service, never to an events list the widget doesn't offer.
      const hostService = showEventsTab
        ? null
        : services.find((s) => s.id === hostedEventFrom) ?? null;
      return (
        <EventsPanel
          events={events}
          onAddedToCart={openCartAfterIdentity}
          initialSelectedId={pendingEventId}
          onConsumeInitialSelection={() => setPendingEventId(null)}
          onCta={handleEventCta}
          availabilityBadge={availabilityBadge}
        />
      );
    }
    // Rendered even when the Packages TAB is hidden (packagesEnabled
    // false) — cross-sell surfaces still navigate here; only the tab
    // bars/lists hide the entry point.
    if (id === "__packages__") {
      return (
        <CombosPanel
          combos={combos}
          services={services}
          tenant={tenant}
          activeBrand={activeBrand}
          label={packagesLabel}
          onAddedToCart={openCartAfterIdentity}
          initialSelectedId={pendingComboId}
          onConsumeInitialSelection={() => setPendingComboId(null)}
        />
      );
    }
    if (id === "__functions__" && functions) {
      return (
        <FunctionsPanel
          tenant={tenant}
          functions={functions}
          widget={widget}
          initialFunctionTypeId={pendingFunctionTypeId}
          onConsumeInitialFunctionType={() => setPendingFunctionTypeId(null)}
        />
      );
    }
    if (id === "__gift_cards__") return <GiftCardPurchasePanel tenant={tenant} />;
    if (id === "__enquiries__") {
      return (
        <EnquiryPanel
          tenant={tenant}
          venues={functions?.venues ?? []}
          enquiryServices={enquiryServices}
          initialServiceId={enquiryServiceId}
          heading={
            typeof widget.settings?.enquiry_title === "string"
              ? (widget.settings.enquiry_title as string)
              : undefined
          }
          tagline={
            typeof widget.settings?.enquiry_tagline === "string"
              ? (widget.settings.enquiry_tagline as string)
              : undefined
          }
          kindLabels={
            widget.settings?.enquiry_kind_labels &&
            typeof widget.settings.enquiry_kind_labels === "object"
              ? (widget.settings.enquiry_kind_labels as Record<string, string>)
              : undefined
          }
          chipCase={
            typeof widget.settings?.enquiry_chip_case === "string"
              ? (widget.settings.enquiry_chip_case as string)
              : undefined
          }
        />
      );
    }
    const svc = services.find((s) => s.id === id) ?? null;
    if (!svc) return null;
    const svcEnquire = !showEnquiriesTab
      ? undefined
      : svc.enquiry_enabled
        ? () => handleEnquireService(svc.id)
        : handleEnquire;
    // WidgetLayerVars: a "panel"-scoped brand tint sets its colours on the
    // panel's own <section>; the panel's overlays (gallery, date popover…) are
    // drawn in a layer outside it (WidgetLayer.tsx) and take them from here.
    if (svc.chidesk && svc.chidesk.treatments.length > 0) {
      return (
        <WidgetLayerVars style={brandHeroProps(svc).panelStyle}>
          <ChideskPanel
            key={svc.id}
            service={svc}
            activeBrand={activeBrand}
            {...brandHeroProps(svc)}
            onAddedToCart={openCartAfterIdentity}
          />
        </WidgetLayerVars>
      );
    }
    // Booking V3: an option-led, bookable service that is not dining,
    // accommodation or events and runs no special-date or hosted events books
    // through the availability panel (what → how many → when). Anything else —
    // including an option-led service that is not bookable — keeps
    // ServicePanel, whose early return renders ServiceUnavailable /
    // EventVenuePanel before any option logic, and which renders the
    // hosted-event cards. availabilityBadge is not passed: the panel never
    // shows counts.
    const svcHostedEvents = events.filter((e) => e.service_id === svc.id);
    if (routesToAvailabilityPanel(svc, svcHostedEvents.length)) {
      return (
        <WidgetLayerVars style={brandHeroProps(svc).panelStyle}>
          <AvailabilityPanel
            key={svc.id}
            service={svc}
            tenant={tenant}
            activeBrand={activeBrand}
            {...brandHeroProps(svc)}
            continuingFromServiceName={continuingFrom}
            onDismissContinuing={() => setContinuingFrom(null)}
            onAddedToCart={openCartAfterIdentity}
            onEnquire={svcEnquire}
            initialOptionId={pendingOption?.serviceId === svc.id ? pendingOption.unitId : null}
            onConsumeInitialOption={() => setPendingOption(null)}
          />
        </WidgetLayerVars>
      );
    }
    return (
      <WidgetLayerVars style={brandHeroProps(svc).panelStyle}>
        <ServicePanel
          key={svc.id}
          service={svc}
          activeBrand={activeBrand}
          {...brandHeroProps(svc)}
          hostedEvents={svcHostedEvents}
          onSelectHostedEvent={handleSelectHostedEvent}
          continuingFromServiceName={continuingFrom}
          onDismissContinuing={() => setContinuingFrom(null)}
          onAddedToCart={openCartAfterIdentity}
          onOpenCart={() => setCartOpen(true)}
          availabilityBadge={availabilityBadge}
          onEnquire={svcEnquire}
          tenantTimezone={tenant.timezone}
        />
      </WidgetLayerVars>
    );
  };

  // Automatic packages cross-sell — a quiet strip below a REAL service's
  // booking panel when that service participates in ≥1 bookable combo.
  // Rendered alongside renderPanel(id) in every layout path, so it takes
  // the same id renderPanel got; pseudo-tabs (__packages__ itself, Events,
  // …) never show it. Rows navigate to the __packages__ browsing panel —
  // which works even when the Packages tab is hidden. Tenants opt out via
  // settings.packages_auto_cross_sell = false.
  const renderPackagesStrip = (id: string | null) => {
    if (!packagesAutoCrossSell || combos.length === 0) return null;
    if (id === null || id.startsWith("__")) return null;
    const svc = services.find((s) => s.id === id);
    if (!svc) return null;
    const matching = combos
      .filter((c) => c.combo_components.some((cc) => cc.service_id === svc.id))
      .slice(0, 3);
    if (matching.length === 0) return null;
    return (
      <section className="vt-panel-block vt-packages-strip">
        <h3 className="vt-panel-heading">{packagesLabel}</h3>
        <ul className="vt-panel-list">
          {matching.map((combo) => (
            <li key={combo.id} className="vt-panel-row-split">
              <button
                type="button"
                className="vt-panel-row-body-btn"
                onClick={() => {
                  setPendingComboId(combo.id);
                  handleSwitchService("__packages__", svc.id);
                }}
              >
                <span className="vt-panel-row-body">
                  <span className="vt-panel-row-title">{combo.name}</span>
                  <span className="vt-panel-row-sub">
                    {combo.description}
                    <span className="vt-panel-row-price-inline">
                      {combo.description ? " · " : ""}
                      {comboPricingLabel(combo)}
                    </span>
                    {/* What it takes to qualify — the days it runs, the party
                        size, the minimum stay, which stays it covers. Without
                        this the strip advertises "50% off" and the guest finds
                        out the conditions only after picking it. */}
                    {comboConditionsLabel(combo) && (
                      <span className="vt-panel-row-conditions">
                        {" · "}
                        {comboConditionsLabel(combo)}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>
    );
  };

  // Tab / pseudo-tab visibility (shared by the TabBar and accordion rows).
  const showFunctionsTab =
    (widget.functions_tab_enabled ?? true) &&
    (functions?.venues.length ?? 0) > 0;
  const showGiftCardsTab =
    (widget.settings?.gift_cards_tab_enabled as boolean | undefined) === true;
  const showEnquiriesTab =
    (widget.settings?.enquire_tab_enabled as boolean | undefined) !== false;

  // Services the tenant marked enquiry-enabled — offered as subjects on the
  // enquiry form and shown an Enquire CTA on their own panel.
  const enquiryServices = services
    .filter((s) => s.enquiry_enabled)
    .map((s) => ({ id: s.id, name: s.name }));

  // Ordered nav list for the accordion layout — services then the same
  // pseudo-tabs the TabBar shows, in the same order.
  const accordionItems: Array<{
    id: string;
    label: string;
    icon: React.ReactNode;
  }> = [
    ...services.map((s) => ({
      id: s.id,
      label: s.name,
      icon: <ServiceIcon type={s.service_type} customSvg={s.icon_svg} />,
    })),
    ...(showEventsTab
      ? [
          {
            id: "__events__",
            label: eventsLabel,
            icon: <AccIcon kind="events" />,
          },
        ]
      : []),
    ...(combos.length > 0 && packagesEnabled
      ? [
          {
            id: "__packages__",
            label: packagesLabel,
            icon: <AccIcon kind="packages" />,
          },
        ]
      : []),
    ...(showFunctionsTab
      ? [
          {
            id: "__functions__",
            label: widget.functions_tab_label?.trim() || "Functions",
            icon: <AccIcon kind="functions" />,
          },
        ]
      : []),
    ...(showGiftCardsTab
      ? [
          {
            id: "__gift_cards__",
            label: "Gift cards",
            icon: <AccIcon kind="gift" />,
          },
        ]
      : []),
    ...(showEnquiriesTab
      ? [
          {
            id: "__enquiries__",
            label: enquireLabel,
            icon: <AccIcon kind="enquire" />,
          },
        ]
      : []),
  ];

  // Landing-tab safety net. The configured default tab (settings.default_tab)
  // — and a Paystack return, which is a fresh mount landing here — can point
  // at a tab that no longer exists at runtime: a service that's been disabled,
  // or a `__packages__` / `__events__` pseudo-tab whose content isn't
  // available. `accordionItems` is the authoritative, ordered set of every
  // navigable tab (services + Events/Packages/Functions/Gift cards/Enquire),
  // so if `activeId` isn't one of them we fall back to the first real tab —
  // unless it is __events__ / __packages__ with something to show
  // (keepsActiveTab): a hosted event's "Book" and the package links go there
  // on purpose with the tab hidden, and resetting them left the tap doing
  // nothing at all. We wait for `combosLoaded` so a valid `__packages__`
  // default isn't reset while its combos are still in flight. This only fires
  // when `activeId` isn't a real tab, so it never fights normal navigation.
  useEffect(() => {
    if (!combosLoaded) return;
    if (activeId === null) return;
    const firstTab = accordionItems[0];
    if (!firstTab) return;
    const shown = accordionItems.map((t) => t.id);
    const content = { events: events.length, combos: combos.length };
    if (keepsActiveTab(activeId, shown, content)) return;
    setActiveId(firstTab.id);
  }, [combosLoaded, accordionItems, activeId, events.length, combos.length]);

  // The id renderPanel/renderPackagesStrip receive on the plain tab and
  // two-column layouts — pseudo-tab ids pass through, a real service
  // resolves via `active` (keeping the services[0] fallback).
  const mainPanelId =
    activeId && activeId.startsWith("__") ? activeId : (active?.id ?? null);

  return (
    <VenueIdentityContext.Provider value={venueIdentity}>
      <Header
        title={displayName}
        tagline={displayTagline}
        logoSrc={logoSrc}
        onOpenCart={() => setCartOpen(true)}
        onOpenAssistant={aiEnabled ? () => setAssistantOpen(true) : null}
        assistantLabel={assistantLabel}
      />

      {layout !== "accordion" &&
        layout !== "catalog-drawer" &&
        layout !== "wizard" &&
        showsTabBar(
          accordionItems.map((t) => t.id),
          activeId,
        ) && (
        <TabBar
          services={services}
          activeId={activeId}
          onSelect={(id) => handleSwitchService(id)}
          showEvents={showEventsTab}
          eventsLabel={eventsLabel}
          showPackages={combos.length > 0 && packagesEnabled}
          packagesLabel={packagesLabel}
          showFunctions={showFunctionsTab}
          functionsLabel={widget.functions_tab_label ?? null}
          functionsIconSvg={widget.functions_tab_icon_svg ?? null}
          showGiftCards={showGiftCardsTab}
          showEnquiries={showEnquiriesTab}
          enquireLabel={enquireLabel}
        />
      )}

      {/* Co-hosting (Phase B): persistent notice while the cart holds a
          required-cohost ticket with no qualifying sibling booking. Lives
          above the layout so every layout variant shows it; clears itself
          the moment the sibling lands in the cart (or the ticket leaves). */}
      <CohostNotice
        onNavigate={(slug) =>
          handleEventCta({ label: "cohost", kind: "service_tab", target: slug })
        }
      />

      <div className={`vt-layout${hasSidebar ? " vt-layout-two-col" : ""}`}>
        <div
          ref={mainPanelRef}
          className={`vt-panel-wrap${
            layout === "accordion"
              ? " vt-accordion"
              : layout === "catalog-drawer"
                ? " vt-catalog"
                : layout === "wizard"
                  ? " vt-wizard"
                  : ""
          }`}
        >
          {layout === "catalog-drawer" ? (
            <div className="vt-catalog-grid">
              {accordionItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`vt-catalog-card${activeId === item.id && drawerOpen ? " vt-catalog-active" : ""}`}
                  onClick={() => {
                    handleSwitchService(item.id);
                    setDrawerOpen(true);
                  }}
                >
                  <span className="vt-catalog-card-icon" aria-hidden>
                    {item.icon}
                  </span>
                  <span className="vt-catalog-card-name">{item.label}</span>
                  <span className="vt-catalog-card-go" aria-hidden>
                    Book →
                  </span>
                </button>
              ))}
            </div>
          ) : layout === "wizard" ? (
            <div className="vt-wizard-flow">
              <div className="vt-wizard-steps">
                {["Choose", "Book", "Enhance"].map((label, i) => (
                  <button
                    key={label}
                    type="button"
                    className={`vt-wizard-step${wizardStep === i ? " vt-wizard-current" : ""}${wizardStep > i ? " vt-wizard-done" : ""}`}
                    disabled={i > 0 && !activeId}
                    onClick={() => {
                      if (i === 0 || activeId) setWizardStep(i);
                    }}
                  >
                    <span className="vt-wizard-num">{i + 1}</span>
                    <span className="vt-wizard-lbl">{label}</span>
                  </button>
                ))}
              </div>
              {wizardStep === 0 ? (
                <div className="vt-wizard-choose">
                  {accordionItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`vt-wizard-choice${activeId === item.id ? " vt-wizard-chosen" : ""}`}
                      onClick={() => {
                        handleSwitchService(item.id);
                        setWizardStep(1);
                      }}
                    >
                      <span className="vt-wizard-choice-icon" aria-hidden>
                        {item.icon}
                      </span>
                      <span className="vt-wizard-choice-name">{item.label}</span>
                      <span className="vt-wizard-choice-go" aria-hidden>
                        →
                      </span>
                    </button>
                  ))}
                </div>
              ) : wizardStep === 1 ? (
                <div className="vt-wizard-stage">
                  {renderPanel(activeId)}
                  {renderPackagesStrip(activeId)}
                </div>
              ) : (
                <div className="vt-wizard-stage vt-wizard-addons">
                  {[...sidebarTop, ...belowMain, ...sidebarBottom, ...footerPanels].length === 0 ? (
                    <div className="vt-wizard-empty">
                      No add-ons here — open your booking to check out.
                    </div>
                  ) : (
                    [...sidebarTop, ...belowMain, ...sidebarBottom, ...footerPanels].map((p) => (
                      <PanelBlock
                        key={p.id}
                        panel={p}
                        services={services}
                        activeService={active}
                        pseudoTabs={pseudoTabs}
                        functions={functions}
                        events={events}
                        combos={combos}
                        packagesLabel={packagesLabel}
                        onSwitchService={handleSwitchService}
                        onQuickAdd={handleQuickAdd}
                        onSelectFunctionType={handleSelectFunctionType}
                        onOpenPackages={(comboId) => {
                          setPendingComboId(comboId ?? null);
                          handleSwitchService("__packages__", active?.id ?? null);
                        }}
                      />
                    ))
                  )}
                </div>
              )}
              <div className="vt-wizard-nav">
                {wizardStep > 0 ? (
                  <button
                    type="button"
                    className="vt-wizard-back"
                    onClick={() => setWizardStep(wizardStep - 1)}
                  >
                    ← Back
                  </button>
                ) : (
                  <span />
                )}
                {wizardStep === 1 && (
                  <button
                    type="button"
                    className="vt-wizard-next"
                    onClick={() => setWizardStep(2)}
                  >
                    Add-ons →
                  </button>
                )}
                {wizardStep === 2 && (
                  <button
                    type="button"
                    className="vt-wizard-next"
                    onClick={() => setCartOpen(true)}
                  >
                    View booking →
                  </button>
                )}
              </div>
            </div>
          ) : layout === "accordion" ? (
            <>
            {accordionItems.map((item) => {
              const open = activeId === item.id;
              return (
                <div
                  key={item.id}
                  className={`vt-acc-item${open ? " vt-acc-open" : ""}`}
                >
                  <button
                    type="button"
                    className="vt-acc-summary"
                    aria-expanded={open}
                    onClick={() => handleSwitchService(item.id)}
                  >
                    <span className="vt-acc-icon" aria-hidden>
                      {item.icon}
                    </span>
                    <span className="vt-acc-name">{item.label}</span>
                    <svg
                      className="vt-acc-chev"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                  {open && (
                    <div className="vt-acc-body">
                      {renderPanel(item.id)}
                      {renderPackagesStrip(item.id)}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Pseudo-tab (Events/Packages) targets aren't accordion rows
                when their tab is disabled — e.g. a service's "What's on" or an
                event-only venue's "Book" jumps to __events__ with
                show_events_tab off (the safety net keeps it: keepsActiveTab).
                Render the target panel below so it never dead-ends. */}
            {activeId != null &&
              activeId.startsWith("__") &&
              !accordionItems.some((i) => i.id === activeId) && (
                <div className="vt-acc-body">{renderPanel(activeId)}</div>
              )}
            </>
          ) : (
            // Every layout renders the active panel through renderPanel() — the
            // tab/accordion/wizard/drawer paths share ONE implementation, so the
            // ChideskPanel/ServicePanel/pseudo-tab branches can't drift apart
            // again. (This block used to be a hand-duplicated copy of
            // renderPanel; it silently dropped per-tab brand colours AND event
            // CTAs on this two-column layout because it wasn't kept in sync.)
            // Pseudo-tabs pass their id through; a real service passes the
            // resolved `active`, which keeps the services[0] fallback.
            // renderPanel keys the panel by service id, preserving the remount
            // (mode reset) behaviour on tab switch.
            <>
              {renderPanel(mainPanelId)}
              {renderPackagesStrip(mainPanelId)}
            </>
          )}
          {layout !== "wizard" &&
            belowMain.map((p) => (
              <PanelBlock
                key={p.id}
                panel={p}
                services={services}
                activeService={active}
                pseudoTabs={pseudoTabs}
                functions={functions}
                events={events}
                combos={combos}
                packagesLabel={packagesLabel}
                onSwitchService={handleSwitchService}
                onQuickAdd={handleQuickAdd}
                onSelectFunctionType={handleSelectFunctionType}
                onOpenPackages={(comboId) => {
                  setPendingComboId(comboId ?? null);
                  handleSwitchService("__packages__", active?.id ?? null);
                }}
              />
            ))}
        </div>
        {hasSidebar && layout !== "wizard" && (
          <aside className="vt-sidebar">
            {sidebarTop.map((p) => (
              <PanelBlock
                key={p.id}
                panel={p}
                services={services}
                activeService={active}
                pseudoTabs={pseudoTabs}
                functions={functions}
                events={events}
                combos={combos}
                packagesLabel={packagesLabel}
                onSwitchService={handleSwitchService}
                onQuickAdd={handleQuickAdd}
                onSelectFunctionType={handleSelectFunctionType}
                onOpenPackages={(comboId) => {
                  setPendingComboId(comboId ?? null);
                  handleSwitchService("__packages__", active?.id ?? null);
                }}
              />
            ))}
            {sidebarBottom.map((p) => (
              <PanelBlock
                key={p.id}
                panel={p}
                services={services}
                activeService={active}
                pseudoTabs={pseudoTabs}
                functions={functions}
                events={events}
                combos={combos}
                packagesLabel={packagesLabel}
                onSwitchService={handleSwitchService}
                onQuickAdd={handleQuickAdd}
                onSelectFunctionType={handleSelectFunctionType}
                onOpenPackages={(comboId) => {
                  setPendingComboId(comboId ?? null);
                  handleSwitchService("__packages__", active?.id ?? null);
                }}
              />
            ))}
          </aside>
        )}
      </div>

      {footerPanels.length > 0 && layout !== "wizard" && (
        <div className="vt-panel-footer">
          {footerPanels.map((p) => (
            <PanelBlock
              key={p.id}
              panel={p}
              services={services}
              activeService={active}
              pseudoTabs={pseudoTabs}
              functions={functions}
              events={events}
              combos={combos}
              packagesLabel={packagesLabel}
              onSwitchService={handleSwitchService}
              onQuickAdd={handleQuickAdd}
              onSelectFunctionType={handleSelectFunctionType}
              onOpenPackages={(comboId) => {
                setPendingComboId(comboId ?? null);
                handleSwitchService("__packages__", active?.id ?? null);
              }}
            />
          ))}
        </div>
      )}

      {/* Catalog + Drawer: the chosen service's booking panel slides in. */}
      {layout === "catalog-drawer" && drawerOpen && (
        <WidgetLayer>
          <div className="vt-drawer-overlay" onClick={() => setDrawerOpen(false)}>
            <div
              className="vt-drawer"
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="vt-drawer-close"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
              <div className="vt-drawer-body">
                {renderPanel(activeId)}
                {renderPackagesStrip(activeId)}
              </div>
            </div>
          </div>
        </WidgetLayer>
      )}

      {/* A sticky booking bar. The pinned-bar layout shows it always as its
          primary nav; every other layout shows a "docked" version that appears
          once the booking has items, giving guests a one-tap way to check out
          from the bottom of the page. */}
      <PinnedCartBar
        currency={tenant.currency ?? "ZAR"}
        onOpen={() => setCartOpen(true)}
        variant={layout === "pinned-bar" ? "pinned" : "docked"}
      />

      <Footer />

      <ChideskResumeModal tenantSlug={tenant.slug} />
      <ToastContainer />

      {cartOpen && (
        <WidgetLayer>
          <CartPanel
            tenant={tenant}
            serviceAddons={serviceAddonsMap}
            onAddSpaTreatment={(serviceId) => {
              setCartOpen(false);
              checkoutReturn.reset();
              handleSwitchService(serviceId, activeId);
              if (layout === "catalog-drawer") setDrawerOpen(true);
              if (layout === "wizard") setWizardStep(1);
            }}
            onClose={() => {
              setCartOpen(false);
              checkoutReturn.reset();
            }}
            // Post-booking "Done" (never backdrop/×/empty-cart): redirect to the
            // tenant's success tab if one is set, else close as normal.
            onConfirmedClose={() => {
              if (configuredSuccessTab) {
                const t = resolveLandingTab(
                  configuredSuccessTab,
                  showEventsTab,
                  services,
                  defaultService,
                );
                if (t) setActiveId(t);
              }
              setCartOpen(false);
              checkoutReturn.reset();
            }}
            // Cohost gate's "Add your {service} booking": close the drawer
            // and drop the guest into that service's booking flow.
            onNavigateToService={(slug) => {
              setCartOpen(false);
              checkoutReturn.reset();
              handleEventCta({
                label: "cohost",
                kind: "service_tab",
                target: slug,
              });
            }}
            // When the customer just returned from Paystack with a
            // successful payment, mount the drawer directly in the
            // confirmed view with the pre-loaded snapshot. Skip the cart
            // and checkout steps — those happened before redirect.
            // Back from Paystack or a restored checkout: the drawer opens in
            // its own view (each a fresh mount, keyed).
            key={checkoutReturn.panelProps.key}
            initialView={checkoutReturn.panelProps.initialView}
            initialConfirmation={checkoutReturn.panelProps.initialConfirmation}
            resume={checkoutReturn.panelProps.resume}
            paymentReturn={checkoutReturn.panelProps.paymentReturn}
            checkoutLayouts={checkoutLayouts}
            presentation={presentation}
            summaryImages={summaryImages}
          />
        </WidgetLayer>
      )}
      {assistantOpen && (
        <WidgetLayer>
          <AssistantModal
            tenantSlug={tenantSlug}
            widgetSlug={widgetSlug}
            onClose={() => setAssistantOpen(false)}
            onOpenCheckout={() => setCartOpen(true)}
          />
        </WidgetLayer>
      )}
    </VenueIdentityContext.Provider>
  );
}

/**
 * Co-hosting (Phase B) notice — derives its visibility straight from the
 * cart: shown while any REQUIRED-cohost ticket line has no qualifying
 * sibling booking (same pairing the cart gate + server run), so it
 * appears on auto-guide after a ticket add, survives tab switches, and
 * disappears by itself once the required booking lands in the cart.
 */
function CohostNotice({ onNavigate }: { onNavigate: (slug: string) => void }) {
  const { items } = useCart();
  const unmet = useMemo(() => computeCohostPairing(items).unmet, [items]);
  const first = unmet[0];
  if (!first) return null;
  return (
    <div className="vt-event-cohost vt-cohost-banner" role="alert">
      <p className="vt-event-cohost-text">
        {cohostNoticeText(first.cohost, first.eventName)}
      </p>
      <button
        type="button"
        className="vt-cta-inline"
        onClick={() => onNavigate(first.cohost.required_service_slug)}
      >
        Add your {first.cohost.required_service_name} booking &rarr;
      </button>
    </div>
  );
}

function Header({
  title,
  tagline,
  logoSrc,
  onOpenCart,
  onOpenAssistant,
  assistantLabel,
}: {
  title: string;
  tagline: string | null;
  logoSrc: string | null;
  onOpenCart: () => void;
  onOpenAssistant: (() => void) | null;
  assistantLabel: string;
}) {
  const { itemCount } = useCart();
  // Draw the eye to the cart: a one-shot "pop" each time an item is added
  // (clear feedback that something landed in the cart), plus a gentle ongoing
  // pulse while the cart is non-empty so guests know to check out.
  const [bump, setBump] = useState(false);
  const prevCount = useRef(itemCount);
  useEffect(() => {
    if (itemCount > prevCount.current) {
      setBump(true);
      const t = setTimeout(() => setBump(false), 600);
      prevCount.current = itemCount;
      return () => clearTimeout(t);
    }
    prevCount.current = itemCount;
  }, [itemCount]);
  return (
    <header className="vt-header">
      <VenueMark identity={{ title, tagline, logoSrc }} />
      <div className="vt-header-actions">
        {onOpenAssistant && (
          <button
            type="button"
            className="vt-assistant-btn"
            onClick={onOpenAssistant}
            aria-label={`${assistantLabel} — open reservation assistant`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M12 2a10 10 0 00-8 16l-1 4 4-1a10 10 0 108-19z" />
              <path d="M8 12h.01M12 12h.01M16 12h.01" strokeLinecap="round" />
            </svg>
            {/* Wrapped so a long tenant label can ellipsis instead of forcing
                the header wider than the viewport. The icon always stays. */}
            <span className="vt-assistant-btn-label">{assistantLabel}</span>
          </button>
        )}
        <button
          type="button"
          className={`vt-cart-btn${itemCount > 0 ? " vt-cart-btn-active" : ""}${bump ? " vt-cart-btn-bump" : ""}`}
          onClick={onOpenCart}
          aria-label={`Open your booking (${itemCount} items)`}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={18} height={18}>
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4H6z" />
            <path d="M3 6h18M16 10a4 4 0 01-8 0" />
          </svg>
          <span className="vt-cart-btn-label">Booking</span>
          <span
            className={`vt-cart-badge${itemCount > 0 ? "" : " vt-cart-badge-empty"}`}
            aria-hidden={itemCount === 0}
          >
            {itemCount}
          </span>
        </button>
      </div>
    </header>
  );
}

function TabBar({
  services,
  activeId,
  onSelect,
  showEvents,
  eventsLabel,
  showPackages,
  packagesLabel,
  showFunctions,
  functionsLabel,
  functionsIconSvg,
  showGiftCards,
  showEnquiries,
  enquireLabel,
}: {
  services: WidgetService[];
  activeId: string | null;
  onSelect: (id: string) => void;
  showEvents?: boolean;
  // Tenant-set override for the Events tab label (settings.
  // events_tab_label). Null/undefined = the default "Events".
  eventsLabel?: string | null;
  showPackages?: boolean;
  // Tenant-set override for the Packages tab label (settings.
  // packages_tab_label). Null/undefined = the default "Packages".
  packagesLabel?: string | null;
  showFunctions?: boolean;
  // Tenant-set overrides for the Functions tab. Null = fall back to
  // the historical label "Functions" + champagne-flute SVG.
  functionsLabel?: string | null;
  functionsIconSvg?: string | null;
  showGiftCards?: boolean;
  showEnquiries?: boolean;
  // Tenant-set override for the Enquire tab label (settings.
  // enquire_tab_label). Null/undefined = the default "Enquire".
  enquireLabel?: string | null;
}) {
  return (
    <nav className="vt-tabbar" role="tablist">
      <div className="vt-tabs">
        {services.map((s) => {
          const active = s.id === activeId;
          return (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`vt-tab${active ? " vt-tab-active" : ""}`}
              onClick={() => onSelect(s.id)}
            >
              <span className="vt-tab-icon">
                <ServiceIcon type={s.service_type} customSvg={s.icon_svg} />
              </span>
              <span className="vt-tab-label">{s.name}</span>
            </button>
          );
        })}
        {showEvents && (
          <button
            type="button"
            role="tab"
            aria-selected={activeId === "__events__"}
            className={`vt-tab${activeId === "__events__" ? " vt-tab-active" : ""}`}
            onClick={() => onSelect("__events__")}
          >
            <span className="vt-tab-icon" aria-hidden>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </span>
            <span className="vt-tab-label">
              {eventsLabel?.trim() || "Events"}
            </span>
          </button>
        )}
        {showPackages && (
          <button
            type="button"
            role="tab"
            aria-selected={activeId === "__packages__"}
            className={`vt-tab${activeId === "__packages__" ? " vt-tab-active" : ""}`}
            onClick={() => onSelect("__packages__")}
          >
            <span className="vt-tab-icon" aria-hidden>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 8v13H3V8" />
                <path d="M1 3h22v5H1z" />
                <path d="M10 12h4" />
              </svg>
            </span>
            <span className="vt-tab-label">
              {packagesLabel?.trim() || "Packages"}
            </span>
          </button>
        )}
        {showFunctions && (
          <button
            type="button"
            role="tab"
            aria-selected={activeId === "__functions__"}
            className={`vt-tab${activeId === "__functions__" ? " vt-tab-active" : ""}`}
            onClick={() => onSelect("__functions__")}
          >
            <span
              className="vt-tab-icon"
              aria-hidden
              // Tenant-supplied SVG when set; the dangerouslySetInnerHTML
              // is intentional — the value is editable only by an
              // authenticated tenant admin and is already trusted markup
              // (the brand/logo upload paths use the same pattern). For
              // the default (no override) we render the historical
              // champagne-flute glyph as JSX.
              {...(functionsIconSvg
                ? { dangerouslySetInnerHTML: { __html: functionsIconSvg } }
                : {})}
            >
              {!functionsIconSvg && (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M8 2h8" />
                  <path d="M12 22V12" />
                  <path d="M8 22h8" />
                  <path d="M6 2c.5 4 2 7 6 7s5.5-3 6-7" />
                </svg>
              )}
            </span>
            <span className="vt-tab-label">
              {functionsLabel?.trim() || "Functions"}
            </span>
          </button>
        )}
        {showGiftCards && (
          <button
            type="button"
            role="tab"
            aria-selected={activeId === "__gift_cards__"}
            className={`vt-tab${activeId === "__gift_cards__" ? " vt-tab-active" : ""}`}
            onClick={() => onSelect("__gift_cards__")}
          >
            <span className="vt-tab-icon" aria-hidden>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 12 20 22 4 22 4 12" />
                <rect x="2" y="7" width="20" height="5" />
                <line x1="12" y1="22" x2="12" y2="7" />
                <path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z" />
                <path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z" />
              </svg>
            </span>
            <span className="vt-tab-label">Gift cards</span>
          </button>
        )}
        {showEnquiries && (
          <button
            type="button"
            role="tab"
            aria-selected={activeId === "__enquiries__"}
            className={`vt-tab${activeId === "__enquiries__" ? " vt-tab-active" : ""}`}
            onClick={() => onSelect("__enquiries__")}
          >
            <span className="vt-tab-icon" aria-hidden>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22 6 12 13 2 6" />
              </svg>
            </span>
            <span className="vt-tab-label">
              {enquireLabel?.trim() || "Enquire"}
            </span>
          </button>
        )}
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <div className="vt-footer">
      Powered by{" "}
      {/* Opens in a NEW tab on purpose: the widget is embedded in the tenant's
          own page and a guest may be mid-booking with items in their cart.
          Navigating away in-place would lose that. rel guards against
          window.opener access from the new tab. */}
      <a
        className="vt-brand vt-brand-link"
        href="https://vertu.co.za"
        target="_blank"
        rel="noopener noreferrer"
        title="Vertu UBS (Pty) Ltd — booking software for hospitality"
      >
        <span className="vt-brand-mark" aria-hidden="true">
          V<span className="vt-brand-dot">.</span>
        </span>
        <strong>Vertu</strong>
      </a>{" "}
      · Payments by <strong>Paystack</strong>
    </div>
  );
}

// A sticky booking summary that follows the customer as content scrolls;
// tapping it opens the booking drawer. Two variants:
//   • "pinned"  — always shown (the pinned-bar layout's primary nav).
//   • "docked"  — shown in every other layout ONLY once the booking has
//     items, so guests always have a one-tap "Complete booking" at the
//     bottom without a permanent bar when the booking is empty.
// Exported for the rendered check's fixture (its "Complete booking →" is held
// to the one primary button style).
export function PinnedCartBar({
  currency,
  onOpen,
  variant = "pinned",
}: {
  currency: string;
  onOpen: () => void;
  variant?: "pinned" | "docked";
}) {
  const { itemCount, subtotal } = useCart();
  // The booking link's promo code, once checked: the price after it, with
  // the price before struck through (only where that total is what the
  // checkout charges now: totalAfterLinkPromo).
  const linkPromo = useLinkPromoPreview();
  const after = totalAfterLinkPromo(Math.round(subtotal * 100), linkPromo);
  if (variant === "docked" && itemCount === 0) return null;
  return (
    <button
      type="button"
      className={`vt-pinned-bar vt-pinned-${variant}${itemCount > 0 ? " vt-pinned-active" : ""}`}
      onClick={onOpen}
    >
      <span className="vt-pinned-info">
        <span className="vt-pinned-count">
          {itemCount > 0
            ? `${itemCount} item${itemCount === 1 ? "" : "s"} in your booking`
            : "Your booking is empty"}
        </span>
        <span className="vt-pinned-total">
          {after !== null ? (
            <>
              <s className="vt-promo-was">
                <span className="vt-sr-only">Was </span>
                {formatDue(currency, Math.round(subtotal * 100))}
              </s>{" "}
              {formatDue(currency, after)}
              <span className="vt-sr-only"> with {linkPromo!.code}</span>
            </>
          ) : (
            <>
              {currency} {subtotal.toLocaleString()}
            </>
          )}
        </span>
      </span>
      <span className="vt-pinned-cta">{itemCount > 0 ? "Complete booking →" : "Browse →"}</span>
    </button>
  );
}
